import * as React from "react"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import bem from "../../../bem"
import ProgressBar, { ProgressBarColor } from "./ProgressBar"

import "./NotificationMessage.scss"
import { INotificationMessage } from "../index"

const b = bem.with("NewUI__notificationMessage")

interface Props {
    data: INotificationMessage
    dismiss: (message: INotificationMessage) => void
    expand: (message: INotificationMessage, expanded: boolean) => void
}

interface State {
    expanded: boolean
    hasExpand: boolean
}

interface TypeColorMapping {
    [NotificationType: string]: ProgressBarColor
}

const TypeColorMapping: TypeColorMapping = {
    error: "red",
    information: "green",
    warning: "orange",
}

const maxHeight = 195

export default class NotificationMessage extends React.Component<Props, State> {
    content: React.RefObject<HTMLDivElement>

    constructor(props: Props) {
        super(props)
        this.state = {
            hasExpand: false,
            expanded: !!props.data.isExpanded,
        }
        this.content = React.createRef()
    }

    render(): JSX.Element {
        const { data } = this.props
        const { hasExpand, expanded } = this.state

        const messageContent = data.isContentHtml ? (
            <div
                data-ft-content
                className="tw-whitespace-pre-line"
                dangerouslySetInnerHTML={{ __html: data.content }}
            />
        ) : (
            <div data-ft-content className="tw-whitespace-pre-line">
                {data.content}
            </div>
        )
        const attributes: Record<string, boolean> = {}
        if (data.attrs) {
            data.attrs.forEach((a) => {
                attributes[`data-ft-notification-${a}`] = true
            })
        }

        const contentBubbleClasses =
            b("content", { expanded: expanded.toString() }) +
            "theme-bubble bg-lightest tw-w-full tw-p-3"

        return (
            <div
                className={b({ isFlash: data.isFlashing })}
                data-ft-notification={data.type}
                {...attributes}
            >
                <div className={contentBubbleClasses}>
                    <article ref={this.content}>
                        <ProgressBar color={TypeColorMapping[data.type]} />
                        <div className="tw-flex tw-items-stretch">
                            <div className="tw-flex-1">
                                <hr />
                                {messageContent}
                            </div>
                            <div className={b("action") + "tw-flex"}>
                                <Action
                                    actionType={ActionType.icon}
                                    data-ft-dismiss
                                    onClick={this.dismiss}
                                >
                                    {Symbols.Cancel}
                                </Action>
                            </div>
                        </div>
                    </article>
                    {hasExpand && (
                        <div
                            className={b("expand")}
                            onClick={this.toggleExpanded}
                        >
                            <Action actionType={ActionType.icon}>
                                {expanded
                                    ? Symbols.ExpandDoubleArrowUp
                                    : Symbols.ExpandDoubleArrowDown}
                            </Action>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    dismiss = (): void => {
        const { dismiss, data } = this.props
        dismiss(data)
    }

    componentDidMount(): void {
        if (
            !!this.content.current &&
            this.content.current.clientHeight >= maxHeight
        ) {
            this.setState({
                hasExpand: true,
            })
        }
    }

    toggleExpanded = (): void => {
        const { data, expand } = this.props
        const { expanded } = this.state

        const newExpanded = !expanded
        this.setState({
            expanded: newExpanded,
        })

        expand(data, newExpanded)
    }
}
